import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Users, UserPlus, UserCheck, UserX, MessageCircle, Church, Clock, Check, X, Search, Loader2, Home, Heart, Globe, MapPin, Filter, Ticket } from "lucide-react";
import { 
  getFriends, getFriendRequests, acceptFriendRequest, rejectFriendRequest, cancelFriendRequest, unfriend,
  getFamilyMembers, getFamilyRequests, acceptFamilyRequest, rejectFamilyRequest, cancelFamilyRequest, removeFamilyMember, getFamilyRelationshipTypes, sendFamilyRequest, searchUsers, getParishes, sendFriendRequest, resolveImageUrl, getFriendSuggestions, getDioceses, getRelationshipStatuses, getAvailableLanguages, getReligiousRoles, getParishesByDiocese
} from "@/lib/api";
import { toast } from "sonner";

// ============ FRIEND COMPONENTS ============

function FriendCard({ friend, onMessage, onUnfriend, onAddFamily, familyIds }) {
  const navigate = useNavigate();
  const isFamily = familyIds?.has(friend.user_id);
  
  return (
    <Card className="bg-white border-[#E5E0D5] hover:shadow-md transition-all" data-testid={`friend-card-${friend.user_id}`}>
      <CardContent className="p-4">
        <div className="flex items-center justify-between">
          <div 
            className="flex items-center gap-3 cursor-pointer min-w-0 flex-1"
            onClick={() => navigate(`/profile/${friend.user_id}`)}
          >
            <Avatar className="h-12 w-12 border-2 border-[#E5E0D5] flex-shrink-0">
              <AvatarImage src={friend.picture} />
              <AvatarFallback className="bg-[var(--liturgical-primary)] text-white font-['Nunito']">
                {friend.name?.[0]?.toUpperCase() || "?"}
              </AvatarFallback>
            </Avatar>
            <div className="min-w-0">
              <h3 className="font-['Nunito'] font-semibold text-[#2D2A26] truncate">{friend.name}</h3>
              {friend.parish_name && (
                <p className="text-xs text-[#6B665E] font-['Nunito'] flex items-center gap-1 truncate">
                  <Church className="w-3 h-3 flex-shrink-0" /> {friend.parish_name}
                </p>
              )}
            </div>
          </div>
          <div className="flex items-center gap-1 flex-shrink-0">
            {!isFamily && onAddFamily && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => onAddFamily(friend)}
                className="border-[#E5E0D5] text-[#6B665E] hover:text-[var(--liturgical-secondary)] hover:border-[var(--liturgical-secondary)]"
                title="Add to family"
                data-testid={`add-family-${friend.user_id}`}
              >
                <Home className="w-4 h-4" />
              </Button>
            )}
            <Button
              variant="outline"
              size="sm"
              onClick={() => onMessage(friend.user_id)}
              className="border-[#E5E0D5] text-[#6B665E] hover:text-[var(--liturgical-primary)] hover:border-[var(--liturgical-primary)]"
              title="Send message"
              data-testid={`message-friend-${friend.user_id}`}
            >
              <MessageCircle className="w-4 h-4" />
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => onUnfriend(friend.user_id, friend.name)}
              className="text-[#6B665E] hover:text-red-600 hover:bg-red-50"
              title="Remove friend"
              data-testid={`unfriend-${friend.user_id}`}
            >
              <UserX className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function FriendRequestCard({ request, type, onAccept, onReject, onCancel }) {
  const navigate = useNavigate();
  const isIncoming = type === "incoming";
  const user = isIncoming 
    ? { id: request.user_id, name: request.sender_name, picture: request.sender_picture, parish: request.sender_parish }
    : { id: request.friend_id, name: request.recipient_name, picture: request.recipient_picture, parish: request.recipient_parish };
  
  const timeAgo = (dateStr) => {
    const diff = Date.now() - new Date(dateStr).getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 60) return `${mins}m ago`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `${hrs}h ago`;
    return `${Math.floor(hrs / 24)}d ago`;
  };

  return (
    <Card className="bg-white border-[#E5E0D5]" data-testid={`friend-request-${request.friendship_id}`}>
      <CardContent className="p-3">
        <div className="flex items-center justify-between">
          <div 
            className="flex items-center gap-3 cursor-pointer"
            onClick={() => navigate(`/profile/${user.id}`)}
          >
            <Avatar className="h-10 w-10 border border-[#E5E0D5]">
              <AvatarImage src={user.picture} />
              <AvatarFallback className="bg-[var(--liturgical-primary)] text-white font-['Nunito'] text-sm">
                {user.name?.[0]?.toUpperCase() || "?"}
              </AvatarFallback>
            </Avatar>
            <div>
              <h4 className="font-['Nunito'] font-semibold text-sm text-[#2D2A26]">{user.name}</h4>
              <p className="text-xs text-[#6B665E] font-['Nunito'] flex items-center gap-1">
                <Clock className="w-3 h-3" /> {timeAgo(request.created_at)}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {isIncoming ? (
              <>
                <Button
                  size="sm"
                  onClick={() => onAccept(request.friendship_id)}
                  className="bg-[var(--liturgical-primary)] hover:brightness-90 text-white h-8"
                  data-testid={`accept-friend-${request.friendship_id}`}
                >
                  <Check className="w-4 h-4" />
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => onReject(request.friendship_id)}
                  className="border-[#E5E0D5] text-[#6B665E] hover:text-red-600 hover:border-red-300 h-8"
                  data-testid={`reject-friend-${request.friendship_id}`}
                >
                  <X className="w-4 h-4" />
                </Button>
              </>
            ) : (
              <Button
                variant="outline"
                size="sm"
                onClick={() => onCancel(request.friendship_id)}
                className="border-[#E5E0D5] text-[#6B665E] hover:text-red-600 hover:border-red-300 h-8 text-xs"
                data-testid={`cancel-friend-${request.friendship_id}`}
              >
                Cancel
              </Button>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

// ============ FAMILY COMPONENTS ============

function FamilyMemberCard({ member, onMessage, onRemove }) {
  const navigate = useNavigate();
  
  return (
    <Card className="bg-white border-[#E5E0D5] hover:shadow-md transition-all" data-testid={`family-card-${member.user_id}`}>
      <CardContent className="p-4">
        <div className="flex items-center justify-between">
          <div 
            className="flex items-center gap-3 cursor-pointer"
            onClick={() => navigate(`/profile/${member.user_id}`)}
          >
            <Avatar className="h-12 w-12 border-2 border-[var(--liturgical-secondary)]">
              <AvatarImage src={member.picture} />
              <AvatarFallback className="bg-[var(--liturgical-secondary)] text-white font-['Nunito']">
                {member.name?.[0]?.toUpperCase() || "?"}
              </AvatarFallback>
            </Avatar>
            <div>
              <h3 className="font-['Nunito'] font-semibold text-[#2D2A26]">{member.name}</h3>
              <Badge className="bg-[var(--liturgical-secondary)]/10 text-[var(--liturgical-secondary)] border border-[var(--liturgical-secondary)]/30 text-xs">
                <Home className="w-3 h-3 mr-1" /> {member.relationship_label}
              </Badge>
              {member.parish_name && (
                <p className="text-xs text-[#6B665E] font-['Nunito'] flex items-center gap-1 mt-1">
                  <Church className="w-3 h-3" /> {member.parish_name}
                </p>
              )}
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => onMessage(member.user_id)}
              className="border-[#E5E0D5] text-[#6B665E] hover:text-[var(--liturgical-primary)] hover:border-[var(--liturgical-primary)]"
              data-testid={`message-family-${member.user_id}`}
            >
              <MessageCircle className="w-4 h-4" />
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => onRemove(member.user_id, member.name)}
              className="text-[#6B665E] hover:text-red-600 hover:bg-red-50"
              data-testid={`remove-family-${member.user_id}`}
            >
              <X className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function FamilyRequestCard({ request, type, onAccept, onReject, onCancel }) {
  const navigate = useNavigate();
  const isIncoming = type === "incoming";
  const user = isIncoming 
    ? { id: request.user_id, name: request.sender_name, picture: request.sender_picture, parish: request.sender_parish }
    : { id: request.relative_id, name: request.recipient_name, picture: request.recipient_picture, parish: request.recipient_parish };
  
  const timeAgo = (dateStr) => {
    const diff = Date.now() - new Date(dateStr).getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 60) return `${mins}m ago`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `${hrs}h ago`;
    return `${Math.floor(hrs / 24)}d ago`;
  };

  return (
    <Card className="bg-white border-[#E5E0D5]" data-testid={`family-request-${request.relationship_id}`}>
      <CardContent className="p-3">
        <div className="flex items-center justify-between">
          <div 
            className="flex items-center gap-3 cursor-pointer"
            onClick={() => navigate(`/profile/${user.id}`)}
          >
            <Avatar className="h-10 w-10 border border-[var(--liturgical-secondary)]">
              <AvatarImage src={user.picture} />
              <AvatarFallback className="bg-[var(--liturgical-secondary)] text-white font-['Nunito'] text-sm">
                {user.name?.[0]?.toUpperCase() || "?"}
              </AvatarFallback>
            </Avatar>
            <div>
              <h4 className="font-['Nunito'] font-semibold text-sm text-[#2D2A26]">{user.name}</h4>
              <div className="flex items-center gap-2">
                <Badge className="bg-[var(--liturgical-secondary)]/10 text-[var(--liturgical-secondary)] text-xs border-0">
                  {request.relationship_label || "Family"}
                </Badge>
                <span className="text-xs text-[#6B665E] font-['Nunito'] flex items-center gap-1">
                  <Clock className="w-3 h-3" /> {timeAgo(request.created_at)}
                </span>
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {isIncoming ? (
              <>
                <Button
                  size="sm"
                  onClick={() => onAccept(request.relationship_id)}
                  className="bg-[var(--liturgical-secondary)] hover:brightness-90 text-white h-8"
                  data-testid={`accept-family-${request.relationship_id}`}
                >
                  <Check className="w-4 h-4" />
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => onReject(request.relationship_id)}
                  className="border-[#E5E0D5] text-[#6B665E] hover:text-red-600 hover:border-red-300 h-8"
                  data-testid={`reject-family-${request.relationship_id}`}
                >
                  <X className="w-4 h-4" />
                </Button>
              </>
            ) : (
              <Button
                variant="outline"
                size="sm"
                onClick={() => onCancel(request.relationship_id)}
                className="border-[#E5E0D5] text-[#6B665E] hover:text-red-600 hover:border-red-300 h-8 text-xs"
                data-testid={`cancel-family-${request.relationship_id}`}
              >
                Cancel
              </Button>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function AddFamilyDialog({ onSuccess }) {
  const [open, setOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [relationshipType, setRelationshipType] = useState("");
  const [relationshipTypes, setRelationshipTypes] = useState({ types: [], labels: {} });
  const [sending, setSending] = useState(false);

  useEffect(() => {
    if (open) {
      loadRelationshipTypes();
    }
  }, [open]);

  const loadRelationshipTypes = async () => {
    try {
      const res = await getFamilyRelationshipTypes();
      setRelationshipTypes(res.data);
    } catch {
      toast.error("Failed to load relationship types");
    }
  };

  const handleSearch = async () => {
    if (searchQuery.length < 2) return;
    setSearching(true);
    try {
      const res = await searchUsers(searchQuery);
      setSearchResults(res.data?.users || []);
    } catch {
      toast.error("Search failed");
    }
    setSearching(false);
  };

  const handleSendRequest = async () => {
    if (!selectedUser || !relationshipType) {
      toast.error("Please select a user and relationship type");
      return;
    }
    setSending(true);
    try {
      await sendFamilyRequest(selectedUser.user_id, relationshipType);
      toast.success(`Family request sent to ${selectedUser.name}!`);
      setOpen(false);
      setSelectedUser(null);
      setRelationshipType("");
      setSearchQuery("");
      setSearchResults([]);
      onSuccess?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to send request");
    }
    setSending(false);
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button className="bg-[var(--liturgical-secondary)] hover:brightness-90 text-white font-['Nunito']" data-testid="add-family-btn">
          <UserPlus className="w-4 h-4 mr-2" /> Add Family
        </Button>
      </DialogTrigger>
      <DialogContent className="bg-white border-[#E5E0D5] max-w-md">
        <DialogHeader>
          <DialogTitle className="font-['Crimson_Text'] text-xl text-[#2D2A26] flex items-center gap-2">
            <Home className="w-5 h-5 text-[var(--liturgical-secondary)]" /> Add Family Member
          </DialogTitle>
        </DialogHeader>
        
        <div className="space-y-4">
          {/* Search */}
          <div className="space-y-2">
            <Label className="text-xs font-['Nunito'] text-[#6B665E]">Search for a user</Label>
            <div className="flex gap-2">
              <Input
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && handleSearch()}
                placeholder="Search by name or email..."
                className="border-[#E5E0D5] font-['Nunito'] text-sm"
                data-testid="family-search-input"
              />
              <Button 
                variant="outline" 
                size="sm" 
                onClick={handleSearch}
                disabled={searching}
                className="border-[#E5E0D5]"
              >
                {searching ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
              </Button>
            </div>
          </div>

          {/* Search Results */}
          {searchResults.length > 0 && (
            <div className="space-y-2 max-h-40 overflow-y-auto">
              {searchResults.map((user) => (
                <div 
                  key={user.user_id}
                  onClick={() => setSelectedUser(user)}
                  className={`flex items-center gap-3 p-2 rounded-lg cursor-pointer transition-all ${
                    selectedUser?.user_id === user.user_id 
                      ? 'bg-[var(--liturgical-secondary)]/10 border border-[var(--liturgical-secondary)]' 
                      : 'hover:bg-[#F9F7F2] border border-transparent'
                  }`}
                >
                  <Avatar className="h-8 w-8">
                    <AvatarImage src={user.picture} />
                    <AvatarFallback className="bg-[var(--liturgical-secondary)] text-white text-xs">
                      {user.name?.[0]?.toUpperCase()}
                    </AvatarFallback>
                  </Avatar>
                  <div>
                    <p className="font-['Nunito'] text-sm font-medium text-[#2D2A26]">{user.name}</p>
                    {user.parish_name && (
                      <p className="font-['Nunito'] text-xs text-[#6B665E]">{user.parish_name}</p>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Selected User */}
          {selectedUser && (
            <div className="bg-[#F9F7F2] rounded-lg p-3">
              <p className="text-xs text-[#6B665E] font-['Nunito'] mb-2">Selected:</p>
              <div className="flex items-center gap-2">
                <Avatar className="h-8 w-8">
                  <AvatarImage src={selectedUser.picture} />
                  <AvatarFallback className="bg-[var(--liturgical-secondary)] text-white text-xs">
                    {selectedUser.name?.[0]?.toUpperCase()}
                  </AvatarFallback>
                </Avatar>
                <span className="font-['Nunito'] font-medium text-sm">{selectedUser.name}</span>
              </div>
            </div>
          )}

          {/* Relationship Type */}
          {selectedUser && (
            <div className="space-y-2">
              <Label className="text-xs font-['Nunito'] text-[#6B665E]">Relationship</Label>
              <Select value={relationshipType} onValueChange={setRelationshipType}>
                <SelectTrigger className="border-[#E5E0D5] font-['Nunito']" data-testid="relationship-type-select">
                  <SelectValue placeholder="Select relationship..." />
                </SelectTrigger>
                <SelectContent className="bg-white border-[#E5E0D5]">
                  {relationshipTypes.types?.map((type) => (
                    <SelectItem key={type} value={type} className="font-['Nunito']">
                      {relationshipTypes.labels?.[type] || type}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button 
            variant="outline" 
            onClick={() => setOpen(false)}
            className="border-[#E5E0D5] font-['Nunito']"
          >
            Cancel
          </Button>
          <Button 
            onClick={handleSendRequest}
            disabled={!selectedUser || !relationshipType || sending}
            className="bg-[var(--liturgical-secondary)] hover:brightness-90 text-white font-['Nunito']"
            data-testid="send-family-request-btn"
          >
            {sending ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <UserPlus className="w-4 h-4 mr-2" />}
            Send Request
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function AddFamilyFromFriendDialog({ friend, onClose, onSuccess }) {
  const [relationshipType, setRelationshipType] = useState("");
  const [relationshipTypes, setRelationshipTypes] = useState({ types: [], labels: {} });
  const [sending, setSending] = useState(false);

  useEffect(() => {
    getFamilyRelationshipTypes().then(res => setRelationshipTypes(res.data)).catch(() => {});
  }, []);

  const handleSend = async () => {
    if (!relationshipType) return;
    setSending(true);
    try {
      await sendFamilyRequest({ target_user_id: friend.user_id, relationship_type: relationshipType });
      toast.success(`Family request sent to ${friend.name}`);
      onSuccess();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to send request");
    }
    setSending(false);
  };

  return (
    <Dialog open={true} onOpenChange={onClose}>
      <DialogContent className="bg-white border-[#E5E0D5] sm:max-w-sm">
        <DialogHeader>
          <DialogTitle className="font-['Crimson_Text'] text-lg">Add {friend.name} to Family</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <p className="text-sm text-[#6B665E] font-['Nunito']">What is your relationship?</p>
          <Select value={relationshipType} onValueChange={setRelationshipType}>
            <SelectTrigger className="border-[#E5E0D5] font-['Nunito']">
              <SelectValue placeholder="Select relationship..." />
            </SelectTrigger>
            <SelectContent className="bg-white border-[#E5E0D5]">
              {relationshipTypes.types?.map(type => (
                <SelectItem key={type} value={type} className="font-['Nunito']">
                  {relationshipTypes.labels?.[type] || type}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} className="border-[#E5E0D5] font-['Nunito']">Cancel</Button>
          <Button onClick={handleSend} disabled={!relationshipType || sending}
            className="bg-[var(--liturgical-secondary)] hover:brightness-90 text-white font-['Nunito']">
            {sending ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Home className="w-4 h-4 mr-2" />}
            Send Request
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ============ MEMBERS TAB ============

function MembersTab({ currentUser }) {
  const navigate = useNavigate();
  const [members, setMembers] = useState([]);
  const [suggestions, setSuggestions] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [loading, setLoading] = useState(false);
  const [loadingSuggested, setLoadingSuggested] = useState(true);
  const [searched, setSearched] = useState(false);
  const [countries, setCountries] = useState([]);
  const [diocesesByCountry, setDiocesesByCountry] = useState({});
  const [parishes, setParishes] = useState([]);
  const [selectedCountry, setSelectedCountry] = useState("all");
  const [selectedDiocese, setSelectedDiocese] = useState("all");
  const [selectedParish, setSelectedParish] = useState("all");
  const [dioceseSearch, setDioceseSearch] = useState("");
  const [parishSearch, setParishSearch] = useState("");
  const [showFilters, setShowFilters] = useState(false);

  // Extra filters
  const [gender, setGender] = useState("all");
  const [relationshipStatus, setRelationshipStatus] = useState("all");
  const [language, setLanguage] = useState("all");
  const [ageRange, setAgeRange] = useState("all");
  const [religiousRole, setReligiousRole] = useState("all");
  const [relationshipStatuses, setRelationshipStatuses] = useState([]);
  const [languages, setLanguages] = useState([]);
  const [religiousRoles, setReligiousRoles] = useState([]);

  useEffect(() => {
    getFriendSuggestions(15).then(r => setSuggestions(r.data?.suggestions || [])).catch(() => {}).finally(() => setLoadingSuggested(false));
    getDioceses().then(r => { setCountries(r.data?.countries || []); setDiocesesByCountry(r.data?.dioceses_by_country || {}); }).catch(() => {});
    getRelationshipStatuses().then(r => setRelationshipStatuses(r.data?.statuses || [])).catch(() => {});
    getAvailableLanguages().then(r => setLanguages(r.data?.languages || [])).catch(() => {});
    getReligiousRoles().then(r => setReligiousRoles(r.data?.roles || r.data || [])).catch(() => {});
  }, []);

  useEffect(() => {
    if (selectedDiocese !== "all") {
      getParishesByDiocese(selectedDiocese).then(r => setParishes(r.data?.parishes || [])).catch(() => setParishes([]));
    } else { setParishes([]); }
    setSelectedParish("all");
    setParishSearch("");
  }, [selectedDiocese]);

  useEffect(() => { setSelectedDiocese("all"); setDioceseSearch(""); }, [selectedCountry]);

  const currentDioceses = selectedCountry !== "all" ? (diocesesByCountry[selectedCountry] || []) : [];
  const filteredDioceses = dioceseSearch.trim() ? currentDioceses.filter(d => d.toLowerCase().includes(dioceseSearch.toLowerCase())) : currentDioceses;
  const AGE_RANGES = [
    { label: "All Ages", value: "all" },
    { label: "18-25", value: "18-25" },
    { label: "26-35", value: "26-35" },
    { label: "36-45", value: "36-45" },
    { label: "46-55", value: "46-55" },
    { label: "56-65", value: "56-65" },
    { label: "65+", value: "65-999" },
  ];

  const hasFilters = selectedCountry !== "all" || selectedDiocese !== "all" || selectedParish !== "all" || gender !== "all" || relationshipStatus !== "all" || language !== "all" || ageRange !== "all" || religiousRole !== "all";

  const handleSearch = async () => {
    setLoading(true); setSearched(true);
    try {
      const params = { limit: 40 };
      if (searchQuery.trim()) params.q = searchQuery.trim();
      if (selectedParish !== "all") params.parish_id = selectedParish;
      if (gender !== "all") params.gender = gender;
      if (relationshipStatus !== "all") params.relationship_status = relationshipStatus;
      if (language !== "all") params.language = language;
      if (religiousRole !== "all") params.religious_role = religiousRole;
      if (ageRange !== "all") {
        const [min, max] = ageRange.split("-").map(Number);
        if (min) params.min_age = min;
        if (max && max < 999) params.max_age = max;
      }
      const res = await searchUsers(searchQuery.trim() || "", params);
      let users = (res.data?.users || res.data || []).filter(u => u.user_id !== currentUser?.user_id);
      if (selectedDiocese !== "all" && selectedParish === "all" && parishes.length > 0) {
        const pids = new Set(parishes.map(p => p.parish_id));
        users = users.filter(u => pids.has(u.parish_id));
      }
      setMembers(users);
    } catch { setMembers([]); }
    setLoading(false);
  };

  useEffect(() => { if (searched || selectedParish !== "all") handleSearch(); }, [selectedParish]);

  return (
    <div className="space-y-4">
      <div className="flex flex-col sm:flex-row gap-2">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#6B665E]" />
          <Input value={searchQuery} onChange={e => setSearchQuery(e.target.value)} onKeyDown={e => e.key === "Enter" && handleSearch()}
            placeholder="Search members by name..." className="pl-9 border-[#E5E0D5] font-['Nunito'] text-sm" data-testid="member-search-input" />
        </div>
        <Button variant="outline" onClick={() => setShowFilters(!showFilters)}
          className={`border-[#E5E0D5] font-['Nunito'] text-sm ${showFilters || hasFilters ? 'bg-[var(--liturgical-primary)]/5 border-[var(--liturgical-primary)]/30' : ''}`}>
          <Filter className="w-4 h-4 mr-1.5" />Filters{hasFilters ? ` (${[selectedCountry !== "all", selectedDiocese !== "all", selectedParish !== "all"].filter(Boolean).length})` : ""}
        </Button>
        <Button onClick={handleSearch} disabled={loading} className="bg-[var(--liturgical-primary)] hover:brightness-90 text-white font-['Nunito']">
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Search"}
        </Button>
      </div>

      {showFilters && (
        <Card className="border-[#E5E0D5]"><CardContent className="p-3 space-y-3">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div>
              <label className="text-[10px] font-semibold text-[#6B665E] font-['Nunito'] uppercase mb-1 block">Country</label>
              <select value={selectedCountry} onChange={e => setSelectedCountry(e.target.value)}
                className="w-full h-9 px-2 rounded-md border border-[#E5E0D5] bg-white text-sm font-['Nunito'] focus:outline-none focus:ring-2 focus:ring-[var(--liturgical-primary)]">
                <option value="all">All Countries</option>
                {countries.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
            <div>
              <label className="text-[10px] font-semibold text-[#6B665E] font-['Nunito'] uppercase mb-1 block">Diocese</label>
              {currentDioceses.length > 15 ? (
                <div className="relative">
                  <input value={selectedDiocese !== "all" ? selectedDiocese : dioceseSearch}
                    onChange={e => { setDioceseSearch(e.target.value); setSelectedDiocese("all"); }}
                    placeholder={selectedCountry === "all" ? "Select country first" : "Type to search..."}
                    disabled={selectedCountry === "all"}
                    className="w-full h-9 px-2 rounded-md border border-[#E5E0D5] bg-white text-sm font-['Nunito'] focus:outline-none focus:ring-2 focus:ring-[var(--liturgical-primary)] disabled:opacity-50" />
                  {dioceseSearch && filteredDioceses.length > 0 && selectedDiocese === "all" && (
                    <div className="absolute z-10 top-full left-0 w-full mt-1 bg-white border border-[#E5E0D5] rounded-md shadow-lg max-h-48 overflow-y-auto">
                      {filteredDioceses.slice(0, 20).map(d => (
                        <button key={d} onClick={() => { setSelectedDiocese(d); setDioceseSearch(""); }}
                          className="w-full text-left px-3 py-2 text-sm font-['Nunito'] hover:bg-[#F0EBE0] truncate">{d}</button>
                      ))}
                    </div>
                  )}
                </div>
              ) : (
                <select value={selectedDiocese} onChange={e => setSelectedDiocese(e.target.value)} disabled={selectedCountry === "all"}
                  className="w-full h-9 px-2 rounded-md border border-[#E5E0D5] bg-white text-sm font-['Nunito'] focus:outline-none focus:ring-2 focus:ring-[var(--liturgical-primary)] disabled:opacity-50">
                  <option value="all">{selectedCountry === "all" ? "Select country first" : "All Dioceses"}</option>
                  {currentDioceses.map(d => <option key={d} value={d}>{d}</option>)}
                </select>
              )}
            </div>
            <div>
              <label className="text-[10px] font-semibold text-[#6B665E] font-['Nunito'] uppercase mb-1 block">Parish</label>
              {parishes.length > 15 ? (
                <div className="relative">
                  <input value={selectedParish !== "all" ? parishes.find(p => p.parish_id === selectedParish)?.name || "" : parishSearch}
                    onChange={e => { setParishSearch(e.target.value); setSelectedParish("all"); }}
                    placeholder={selectedDiocese === "all" ? "Select diocese first" : "Type to search..."}
                    disabled={selectedDiocese === "all"}
                    className="w-full h-9 px-2 rounded-md border border-[#E5E0D5] bg-white text-sm font-['Nunito'] focus:outline-none focus:ring-2 focus:ring-[var(--liturgical-primary)] disabled:opacity-50" />
                  {parishSearch && selectedParish === "all" && (
                    <div className="absolute z-10 top-full left-0 w-full mt-1 bg-white border border-[#E5E0D5] rounded-md shadow-lg max-h-48 overflow-y-auto">
                      {parishes.filter(p => p.name.toLowerCase().includes(parishSearch.toLowerCase())).slice(0, 20).map(p => (
                        <button key={p.parish_id} onClick={() => { setSelectedParish(p.parish_id); setParishSearch(""); }}
                          className="w-full text-left px-3 py-2 text-sm font-['Nunito'] hover:bg-[#F0EBE0] truncate">{p.name}</button>
                      ))}
                    </div>
                  )}
                </div>
              ) : (
                <select value={selectedParish} onChange={e => setSelectedParish(e.target.value)} disabled={selectedDiocese === "all"}
                  className="w-full h-9 px-2 rounded-md border border-[#E5E0D5] bg-white text-sm font-['Nunito'] focus:outline-none focus:ring-2 focus:ring-[var(--liturgical-primary)] disabled:opacity-50">
                  <option value="all">{parishes.length > 0 ? "All Parishes" : "Select diocese first"}</option>
                  {parishes.map(p => <option key={p.parish_id} value={p.parish_id}>{p.name}</option>)}
                </select>
              )}
            </div>
          </div>
          {/* Extra filters row */}
          <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
            <div>
              <label className="text-[10px] font-semibold text-[#6B665E] font-['Nunito'] uppercase mb-1 block">Gender</label>
              <select value={gender} onChange={e => setGender(e.target.value)}
                className="w-full h-9 px-2 rounded-md border border-[#E5E0D5] bg-white text-sm font-['Nunito'] focus:outline-none focus:ring-2 focus:ring-[var(--liturgical-primary)]">
                <option value="all">All</option>
                <option value="male">Men</option>
                <option value="female">Women</option>
              </select>
            </div>
            <div>
              <label className="text-[10px] font-semibold text-[#6B665E] font-['Nunito'] uppercase mb-1 block">Status</label>
              <select value={relationshipStatus} onChange={e => setRelationshipStatus(e.target.value)}
                className="w-full h-9 px-2 rounded-md border border-[#E5E0D5] bg-white text-sm font-['Nunito'] focus:outline-none focus:ring-2 focus:ring-[var(--liturgical-primary)]">
                <option value="all">All</option>
                {relationshipStatuses.map(s => <option key={s.id} value={s.id}>{s.label}</option>)}
              </select>
            </div>
            <div>
              <label className="text-[10px] font-semibold text-[#6B665E] font-['Nunito'] uppercase mb-1 block">Language</label>
              <select value={language} onChange={e => setLanguage(e.target.value)}
                className="w-full h-9 px-2 rounded-md border border-[#E5E0D5] bg-white text-sm font-['Nunito'] focus:outline-none focus:ring-2 focus:ring-[var(--liturgical-primary)]">
                <option value="all">All</option>
                {languages.map(l => <option key={l} value={l}>{l}</option>)}
              </select>
            </div>
            <div>
              <label className="text-[10px] font-semibold text-[#6B665E] font-['Nunito'] uppercase mb-1 block">Age</label>
              <select value={ageRange} onChange={e => setAgeRange(e.target.value)}
                className="w-full h-9 px-2 rounded-md border border-[#E5E0D5] bg-white text-sm font-['Nunito'] focus:outline-none focus:ring-2 focus:ring-[var(--liturgical-primary)]">
                {AGE_RANGES.map(a => <option key={a.value} value={a.value}>{a.label}</option>)}
              </select>
            </div>
            <div>
              <label className="text-[10px] font-semibold text-[#6B665E] font-['Nunito'] uppercase mb-1 block">Role</label>
              <select value={religiousRole} onChange={e => setReligiousRole(e.target.value)}
                className="w-full h-9 px-2 rounded-md border border-[#E5E0D5] bg-white text-sm font-['Nunito'] focus:outline-none focus:ring-2 focus:ring-[var(--liturgical-primary)]">
                <option value="all">All</option>
                {religiousRoles.map(r => <option key={r.id} value={r.id}>{r.name}</option>)}
              </select>
            </div>
          </div>
          {hasFilters && (
            <Button variant="ghost" size="sm" onClick={() => { setSelectedCountry("all"); setSelectedDiocese("all"); setSelectedParish("all"); setDioceseSearch(""); setParishSearch(""); setGender("all"); setRelationshipStatus("all"); setLanguage("all"); setAgeRange("all"); setReligiousRole("all"); }}
              className="text-xs text-[var(--liturgical-primary)] font-['Nunito']"><X className="w-3 h-3 mr-1" />Clear all filters</Button>
          )}
        </CardContent></Card>
      )}

      {!searched && !hasFilters ? (
        loadingSuggested ? (
          <div className="text-center py-12 text-[#6B665E] font-['Nunito']">Loading suggestions...</div>
        ) : suggestions.length === 0 ? (
          <div className="text-center py-12">
            <Users className="w-12 h-12 text-[#E5E0D5] mx-auto mb-3" />
            <p className="text-[#6B665E] font-['Nunito']">Search for members or use filters to browse</p>
          </div>
        ) : (
          <div className="space-y-2">
            <p className="text-xs font-semibold text-[#6B665E] font-['Nunito'] uppercase tracking-wide">Suggested for You</p>
            {suggestions.map(s => (
              <Card key={s.user_id} className="bg-white border-[#E5E0D5] hover:shadow-md transition-all cursor-pointer"
                onClick={() => navigate(`/profile/${s.user_id}`)} data-testid={`suggestion-${s.user_id}`}>
                <CardContent className="p-3">
                  <div className="flex items-center gap-3">
                    <Avatar className="h-10 w-10 border border-[#E5E0D5] flex-shrink-0">
                      <AvatarImage src={resolveImageUrl(s.picture)} />
                      <AvatarFallback className="bg-[var(--liturgical-primary)] text-white font-['Nunito'] text-sm">{s.name?.[0]?.toUpperCase()}</AvatarFallback>
                    </Avatar>
                    <div className="flex-1 min-w-0">
                      <h3 className="font-['Nunito'] font-semibold text-sm text-[#2D2A26] truncate">{s.name}</h3>
                      <div className="flex items-center gap-2 flex-wrap">
                        {s.parish_name && <p className="text-xs text-[#6B665E] font-['Nunito'] flex items-center gap-1"><Church className="w-3 h-3" />{s.parish_name}</p>}
                        {s.mutual_count > 0 && <p className="text-xs text-[var(--liturgical-primary)] font-['Nunito'] font-medium">{s.mutual_count} mutual friend{s.mutual_count > 1 ? "s" : ""}</p>}
                      </div>
                      {s.reason && <p className="text-[10px] text-[#9C9689] font-['Nunito']">{s.reason}</p>}
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )
      ) : loading ? (
        <div className="text-center py-12 text-[#6B665E] font-['Nunito']">Searching...</div>
      ) : members.length === 0 ? (
        <div className="text-center py-12">
          <Search className="w-10 h-10 text-[#E5E0D5] mx-auto mb-3" />
          <p className="text-[#6B665E] font-['Nunito']">No members found</p>
        </div>
      ) : (
        <div className="space-y-2">
          <p className="text-xs text-[#6B665E] font-['Nunito']">{members.length} results</p>
          {members.map(member => (
            <Card key={member.user_id} className="bg-white border-[#E5E0D5] hover:shadow-md transition-all cursor-pointer"
              onClick={() => navigate(`/profile/${member.user_id}`)} data-testid={`member-card-${member.user_id}`}>
              <CardContent className="p-3">
                <div className="flex items-center gap-3">
                  <Avatar className="h-10 w-10 border border-[#E5E0D5] flex-shrink-0">
                    <AvatarImage src={resolveImageUrl(member.picture)} />
                    <AvatarFallback className="bg-[var(--liturgical-primary)] text-white font-['Nunito'] text-sm">{member.name?.[0]?.toUpperCase()}</AvatarFallback>
                  </Avatar>
                  <div className="flex-1 min-w-0">
                    <h3 className="font-['Nunito'] font-semibold text-sm text-[#2D2A26] truncate">{member.name}</h3>
                    {member.parish_name && <p className="text-xs text-[#6B665E] font-['Nunito'] flex items-center gap-1 truncate"><Church className="w-3 h-3 flex-shrink-0" />{member.parish_name}</p>}
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}


// ============ MAIN PAGE COMPONENT ============

export default function FriendsAndFamilyPage({ user }) {
  const navigate = useNavigate();
  
  // View toggle: "friends" or "family"
  const [view, setView] = useState("friends");
  
  // Friends state
  const [friends, setFriends] = useState([]);
  const [friendRequests, setFriendRequests] = useState({ incoming: [], outgoing: [] });
  const [friendsTab, setFriendsTab] = useState("list");
  
  // Friends search and filter state
  const [friendSearchQuery, setFriendSearchQuery] = useState("");
  const [friendParishFilter, setFriendParishFilter] = useState("all");
  
  // Family state
  const [familyMembers, setFamilyMembers] = useState([]);
  const [familyRequests, setFamilyRequests] = useState({ incoming: [], outgoing: [] });
  const [familyTab, setFamilyTab] = useState("list");
  
  // Family search state
  const [familySearchQuery, setFamilySearchQuery] = useState("");
  
  const [loading, setLoading] = useState(true);

  // Load functions
  const loadFriends = async () => {
    try {
      const res = await getFriends();
      setFriends(res.data.friends);
    } catch (err) {
      console.error("Failed to load friends:", err);
    }
  };

  const loadFriendRequests = async () => {
    try {
      const res = await getFriendRequests();
      setFriendRequests(res.data);
    } catch (err) {
      console.error("Failed to load friend requests:", err);
    }
  };

  const loadFamilyMembers = async () => {
    try {
      const res = await getFamilyMembers();
      setFamilyMembers(res.data.family);
    } catch (err) {
      console.error("Failed to load family:", err);
    }
  };

  const loadFamilyRequests = async () => {
    try {
      const res = await getFamilyRequests();
      setFamilyRequests(res.data);
    } catch (err) {
      console.error("Failed to load family requests:", err);
    }
  };

  const loadAll = async () => {
    setLoading(true);
    await Promise.all([loadFriends(), loadFriendRequests(), loadFamilyMembers(), loadFamilyRequests()]);
    setLoading(false);
  };

  useEffect(() => {
    loadAll();
  }, []);

  const handleMessage = (userId) => {
    navigate(`/messages/${userId}`);
  };

  // Friend handlers
  const handleUnfriend = async (userId, name) => {
    if (!window.confirm(`Remove ${name} from your friends?`)) return;
    try {
      await unfriend(userId);
      setFriends(friends.filter(f => f.user_id !== userId));
      toast.success(`Removed ${name} from friends`);
    } catch {
      toast.error("Failed to unfriend");
    }
  };

  const handleAcceptFriend = async (friendshipId) => {
    try {
      await acceptFriendRequest(friendshipId);
      toast.success("Friend request accepted!");
      loadFriends();
      loadFriendRequests();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to accept request");
    }
  };

  const handleRejectFriend = async (friendshipId) => {
    try {
      await rejectFriendRequest(friendshipId);
      setFriendRequests({
        ...friendRequests,
        incoming: friendRequests.incoming.filter(r => r.friendship_id !== friendshipId)
      });
      toast.success("Friend request declined");
    } catch {
      toast.error("Failed to reject request");
    }
  };

  const handleCancelFriend = async (friendshipId) => {
    try {
      await cancelFriendRequest(friendshipId);
      setFriendRequests({
        ...friendRequests,
        outgoing: friendRequests.outgoing.filter(r => r.friendship_id !== friendshipId)
      });
      toast.success("Friend request cancelled");
    } catch {
      toast.error("Failed to cancel request");
    }
  };

  // Family handlers
  const handleRemoveFamily = async (userId, name) => {
    if (!window.confirm(`Remove ${name} from your family?`)) return;
    try {
      await removeFamilyMember(userId);
      setFamilyMembers(familyMembers.filter(f => f.user_id !== userId));
      toast.success(`Removed ${name} from family`);
    } catch {
      toast.error("Failed to remove family member");
    }
  };

  const handleAcceptFamily = async (relationshipId) => {
    try {
      await acceptFamilyRequest(relationshipId);
      toast.success("Family request accepted!");
      loadFamilyMembers();
      loadFamilyRequests();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to accept request");
    }
  };

  const handleRejectFamily = async (relationshipId) => {
    try {
      await rejectFamilyRequest(relationshipId);
      setFamilyRequests({
        ...familyRequests,
        incoming: familyRequests.incoming.filter(r => r.relationship_id !== relationshipId)
      });
      toast.success("Family request declined");
    } catch {
      toast.error("Failed to reject request");
    }
  };

  const handleCancelFamily = async (relationshipId) => {
    try {
      await cancelFamilyRequest(relationshipId);
      setFamilyRequests({
        ...familyRequests,
        outgoing: familyRequests.outgoing.filter(r => r.relationship_id !== relationshipId)
      });
      toast.success("Family request cancelled");
    } catch {
      toast.error("Failed to cancel request");
    }
  };

  const totalFriendRequests = friendRequests.incoming.length;
  const totalFamilyRequests = familyRequests.incoming.length;

  // Get unique parishes from friends for filter dropdown
  const friendParishes = [...new Set(friends.filter(f => f.parish_name).map(f => f.parish_name))];

  // Filter friends based on search and parish filter
  const filteredFriends = friends.filter(friend => {
    const matchesSearch = friendSearchQuery.trim() === "" || 
      friend.name?.toLowerCase().includes(friendSearchQuery.toLowerCase());
    const matchesParish = friendParishFilter === "all" || 
      friend.parish_name === friendParishFilter;
    return matchesSearch && matchesParish;
  });

  // Filter family members based on search
  const filteredFamily = familyMembers.filter(member => {
    return familySearchQuery.trim() === "" || 
      member.name?.toLowerCase().includes(familySearchQuery.toLowerCase()) ||
      member.relationship_label?.toLowerCase().includes(familySearchQuery.toLowerCase());
  });

  // Set of family member IDs for quick lookup
  const familyMemberIds = new Set(familyMembers.map(m => m.user_id));

  // Add family from friend card — opens the AddFamilyDialog with pre-selected user
  const [addFamilyTarget, setAddFamilyTarget] = useState(null);

  return (
    <div className="min-h-screen bg-[#F9F7F2]" data-testid="friends-family-page">
      <div className="max-w-3xl mx-auto px-4 py-8 space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <div>
            <h1 className="font-['Crimson_Text'] text-2xl sm:text-3xl font-bold text-[#2D2A26] tracking-tight" data-testid="community-title">
              Community
            </h1>
            <p className="text-sm text-[#6B665E] font-['Nunito'] mt-1">
              {friends.length} friends, {familyMembers.length} family
            </p>
          </div>
          <Button
            onClick={() => navigate("/invite")}
            variant="outline"
            className="border-[var(--liturgical-primary)]/30 text-[var(--liturgical-primary)] hover:bg-[var(--liturgical-primary)]/5 font-['Nunito'] text-sm"
            data-testid="invite-friends-btn"
          >
            <Ticket className="w-4 h-4 mr-1.5" />Invite Friends
          </Button>
        </div>

        {/* Requests Widget */}
        {(totalFriendRequests > 0 || totalFamilyRequests > 0) && (
          <Card className="border-[var(--liturgical-primary)]/30 bg-[var(--liturgical-primary)]/5">
            <CardContent className="p-3">
              <div className="flex flex-wrap items-center gap-3">
                <span className="text-xs font-semibold font-['Nunito'] text-[#2D2A26]">Pending Requests</span>
                {totalFriendRequests > 0 && (
                  <Badge
                    className="bg-[var(--liturgical-primary)] text-white font-['Nunito'] text-xs cursor-pointer hover:brightness-90"
                    onClick={() => { setView("friends"); setFriendsTab("requests"); }}
                    data-testid="friend-request-badge"
                  >
                    <UserPlus className="w-3 h-3 mr-1" />
                    {totalFriendRequests} friend {totalFriendRequests === 1 ? "request" : "requests"}
                  </Badge>
                )}
                {totalFamilyRequests > 0 && (
                  <Badge
                    className="bg-[var(--liturgical-secondary)] text-white font-['Nunito'] text-xs cursor-pointer hover:brightness-90"
                    onClick={() => { setView("family"); setFamilyTab("requests"); }}
                    data-testid="family-request-badge"
                  >
                    <UserPlus className="w-3 h-3 mr-1" />
                    {totalFamilyRequests} family {totalFamilyRequests === 1 ? "request" : "requests"}
                  </Badge>
                )}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Main Tabs */}
        <Tabs value={view} onValueChange={setView}>
          <TabsList className="bg-[#F0EBE0] border border-[#E5E0D5] flex-wrap h-auto">
            <TabsTrigger value="friends" className="font-['Nunito'] text-xs sm:text-sm data-[state=active]:bg-white data-[state=active]:text-[var(--liturgical-primary)]" data-testid="tab-friends">
              <Users className="w-4 h-4 mr-1.5" />Friends ({friends.length})
            </TabsTrigger>
            <TabsTrigger value="family" className="font-['Nunito'] text-xs sm:text-sm data-[state=active]:bg-white data-[state=active]:text-[var(--liturgical-primary)]" data-testid="tab-family">
              <Home className="w-4 h-4 mr-1.5" />Family ({familyMembers.length})
            </TabsTrigger>
            <TabsTrigger value="members" className="font-['Nunito'] text-xs sm:text-sm data-[state=active]:bg-white data-[state=active]:text-[var(--liturgical-primary)]" data-testid="tab-all-members">
              <Globe className="w-4 h-4 mr-1.5" />All Members
            </TabsTrigger>
          </TabsList>

          {/* Friends Tab */}
          <TabsContent value="friends" className="mt-4">
            <div className="space-y-4">
              <Tabs value={friendsTab} onValueChange={setFriendsTab}>
                <TabsList className="bg-[#F0EBE0] border border-[#E5E0D5]">
                  <TabsTrigger value="list" className="font-['Nunito'] text-sm data-[state=active]:bg-white data-[state=active]:text-[var(--liturgical-primary)]" data-testid="tab-friends-list">
                    <UserCheck className="w-4 h-4 mr-1.5" /> My Friends
                  </TabsTrigger>
                  <TabsTrigger value="requests" className="font-['Nunito'] text-sm data-[state=active]:bg-white data-[state=active]:text-[var(--liturgical-primary)] relative" data-testid="tab-friends-requests">
                    <UserPlus className="w-4 h-4 mr-1.5" /> Requests
                    {friendRequests.incoming.length > 0 && (
                      <Badge className="ml-1.5 bg-[var(--liturgical-primary)] text-white text-xs h-5 w-5 p-0 flex items-center justify-center rounded-full">
                        {friendRequests.incoming.length}
                      </Badge>
                    )}
                  </TabsTrigger>
                </TabsList>

                <TabsContent value="list" className="mt-4 space-y-3">
                {/* Search and Filter Bar */}
                {friends.length > 0 && (
                  <div className="flex flex-col sm:flex-row gap-2 mb-4">
                    <div className="relative flex-1">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#6B665E]" />
                      <Input
                        value={friendSearchQuery}
                        onChange={(e) => setFriendSearchQuery(e.target.value)}
                        placeholder="Search friends by name..."
                        className="pl-9 border-[#E5E0D5] font-['Nunito'] text-sm"
                        data-testid="friend-search-input"
                      />
                    </div>
                    {friendParishes.length > 0 && (
                      <Select value={friendParishFilter} onValueChange={setFriendParishFilter}>
                        <SelectTrigger className="w-full sm:w-[180px] border-[#E5E0D5] font-['Nunito'] text-sm" data-testid="friend-parish-filter">
                          <Church className="w-4 h-4 mr-2 text-[#6B665E]" />
                          <SelectValue placeholder="All Parishes" />
                        </SelectTrigger>
                        <SelectContent className="bg-white border-[#E5E0D5]">
                          <SelectItem value="all" className="font-['Nunito']">All Parishes</SelectItem>
                          {friendParishes.map((parish) => (
                            <SelectItem key={parish} value={parish} className="font-['Nunito']">
                              {parish}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    )}
                  </div>
                )}

                {loading ? (
                  <div className="text-center py-12 text-[#6B665E] font-['Nunito']">
                    Loading friends...
                  </div>
                ) : friends.length === 0 ? (
                  <div className="text-center py-12" data-testid="no-friends">
                    <Users className="w-12 h-12 text-[#E5E0D5] mx-auto mb-3" />
                    <p className="text-[#6B665E] font-['Nunito']">No friends yet</p>
                    <p className="text-sm text-[#6B665E] font-['Nunito'] mt-1">
                      Visit other profiles to send friend requests!
                    </p>
                  </div>
                ) : filteredFriends.length === 0 ? (
                  <div className="text-center py-8" data-testid="no-friends-match">
                    <Search className="w-10 h-10 text-[#E5E0D5] mx-auto mb-3" />
                    <p className="text-[#6B665E] font-['Nunito']">No friends match your search</p>
                    <Button 
                      variant="link" 
                      onClick={() => { setFriendSearchQuery(""); setFriendParishFilter("all"); }}
                      className="text-[var(--liturgical-primary)] font-['Nunito'] mt-1"
                    >
                      Clear filters
                    </Button>
                  </div>
                ) : (
                  <>
                    {(friendSearchQuery || friendParishFilter !== "all") && (
                      <p className="text-xs text-[#6B665E] font-['Nunito'] mb-2">
                        Showing {filteredFriends.length} of {friends.length} friends
                      </p>
                    )}
                    {filteredFriends.map((friend) => (
                      <FriendCard
                        key={friend.user_id}
                        friend={friend}
                        onMessage={handleMessage}
                        onUnfriend={handleUnfriend}
                        onAddFamily={(f) => setAddFamilyTarget(f)}
                        familyIds={familyMemberIds}
                      />
                    ))}
                  </>
                )}
              </TabsContent>

              <TabsContent value="requests" className="mt-4 space-y-6">
                {/* Incoming Friend Requests */}
                <div>
                  <h2 className="font-['Nunito'] font-semibold text-[#2D2A26] mb-3 flex items-center gap-2">
                    Incoming Requests
                    {friendRequests.incoming.length > 0 && (
                      <Badge className="bg-[var(--liturgical-primary)] text-white text-xs">{friendRequests.incoming.length}</Badge>
                    )}
                  </h2>
                  {friendRequests.incoming.length === 0 ? (
                    <p className="text-sm text-[#6B665E] font-['Nunito'] py-4">No pending friend requests</p>
                  ) : (
                    <div className="space-y-2">
                      {friendRequests.incoming.map((req) => (
                        <FriendRequestCard
                          key={req.friendship_id}
                          request={req}
                          type="incoming"
                          onAccept={handleAcceptFriend}
                          onReject={handleRejectFriend}
                        />
                      ))}
                    </div>
                  )}
                </div>

                {/* Outgoing Friend Requests */}
                <div>
                  <h2 className="font-['Nunito'] font-semibold text-[#2D2A26] mb-3 flex items-center gap-2">
                    Sent Requests
                    {friendRequests.outgoing.length > 0 && (
                      <Badge className="bg-[var(--liturgical-secondary)] text-white text-xs">{friendRequests.outgoing.length}</Badge>
                    )}
                  </h2>
                  {friendRequests.outgoing.length === 0 ? (
                    <p className="text-sm text-[#6B665E] font-['Nunito'] py-4">No pending sent requests</p>
                  ) : (
                    <div className="space-y-2">
                      {friendRequests.outgoing.map((req) => (
                        <FriendRequestCard
                          key={req.friendship_id}
                          request={req}
                          type="outgoing"
                          onCancel={handleCancelFriend}
                        />
                      ))}
                    </div>
                  )}
                </div>
              </TabsContent>
            </Tabs>
          </div>
          </TabsContent>

          {/* Family Tab */}
          <TabsContent value="family" className="mt-4">
            <div className="space-y-4">
            <Tabs value={familyTab} onValueChange={setFamilyTab}>
              <TabsList className="bg-[#F0EBE0] border border-[#E5E0D5]">
                <TabsTrigger
                  value="list"
                  className="font-['Nunito'] text-sm data-[state=active]:bg-white data-[state=active]:text-[var(--liturgical-secondary)]"
                  data-testid="tab-family-list"
                >
                  <Home className="w-4 h-4 mr-1.5" /> My Family
                </TabsTrigger>
                <TabsTrigger
                  value="requests"
                  className="font-['Nunito'] text-sm data-[state=active]:bg-white data-[state=active]:text-[var(--liturgical-secondary)] relative"
                  data-testid="tab-family-requests"
                >
                  <UserPlus className="w-4 h-4 mr-1.5" /> Requests
                  {familyRequests.incoming.length > 0 && (
                    <Badge className="ml-1.5 bg-[var(--liturgical-secondary)] text-white text-xs h-5 w-5 p-0 flex items-center justify-center rounded-full">
                      {familyRequests.incoming.length}
                    </Badge>
                  )}
                </TabsTrigger>
              </TabsList>

              <TabsContent value="list" className="mt-4 space-y-3">
                {/* Search Bar for Family */}
                {familyMembers.length > 0 && (
                  <div className="relative mb-4">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#6B665E]" />
                    <Input
                      value={familySearchQuery}
                      onChange={(e) => setFamilySearchQuery(e.target.value)}
                      placeholder="Search family by name or relationship..."
                      className="pl-9 border-[#E5E0D5] font-['Nunito'] text-sm"
                      data-testid="family-member-search-input"
                    />
                  </div>
                )}

                {loading ? (
                  <div className="text-center py-12 text-[#6B665E] font-['Nunito']">
                    Loading family members...
                  </div>
                ) : familyMembers.length === 0 ? (
                  <div className="text-center py-12" data-testid="no-family">
                    <Home className="w-12 h-12 text-[#E5E0D5] mx-auto mb-3" />
                    <p className="text-[#6B665E] font-['Nunito']">No family members yet</p>
                    <p className="text-sm text-[#6B665E] font-['Nunito'] mt-1">
                      Click "Add Family" to connect with your relatives!
                    </p>
                  </div>
                ) : filteredFamily.length === 0 ? (
                  <div className="text-center py-8" data-testid="no-family-match">
                    <Search className="w-10 h-10 text-[#E5E0D5] mx-auto mb-3" />
                    <p className="text-[#6B665E] font-['Nunito']">No family members match your search</p>
                    <Button 
                      variant="link" 
                      onClick={() => setFamilySearchQuery("")}
                      className="text-[var(--liturgical-secondary)] font-['Nunito'] mt-1"
                    >
                      Clear search
                    </Button>
                  </div>
                ) : (
                  <>
                    {familySearchQuery && (
                      <p className="text-xs text-[#6B665E] font-['Nunito'] mb-2">
                        Showing {filteredFamily.length} of {familyMembers.length} family members
                      </p>
                    )}
                    {filteredFamily.map((member) => (
                      <FamilyMemberCard
                        key={member.user_id}
                        member={member}
                        onMessage={handleMessage}
                        onRemove={handleRemoveFamily}
                      />
                    ))}
                  </>
                )}
              </TabsContent>

              <TabsContent value="requests" className="mt-4 space-y-6">
                {/* Incoming Family Requests */}
                <div>
                  <h2 className="font-['Nunito'] font-semibold text-[#2D2A26] mb-3 flex items-center gap-2">
                    Incoming Requests
                    {familyRequests.incoming.length > 0 && (
                      <Badge className="bg-[var(--liturgical-secondary)] text-white text-xs">{familyRequests.incoming.length}</Badge>
                    )}
                  </h2>
                  {familyRequests.incoming.length === 0 ? (
                    <p className="text-sm text-[#6B665E] font-['Nunito'] py-4">No pending family requests</p>
                  ) : (
                    <div className="space-y-2">
                      {familyRequests.incoming.map((req) => (
                        <FamilyRequestCard
                          key={req.relationship_id}
                          request={req}
                          type="incoming"
                          onAccept={handleAcceptFamily}
                          onReject={handleRejectFamily}
                        />
                      ))}
                    </div>
                  )}
                </div>

                {/* Outgoing Family Requests */}
                <div>
                  <h2 className="font-['Nunito'] font-semibold text-[#2D2A26] mb-3 flex items-center gap-2">
                    Sent Requests
                    {familyRequests.outgoing.length > 0 && (
                      <Badge className="bg-[var(--liturgical-primary)] text-white text-xs">{familyRequests.outgoing.length}</Badge>
                    )}
                  </h2>
                  {familyRequests.outgoing.length === 0 ? (
                    <p className="text-sm text-[#6B665E] font-['Nunito'] py-4">No pending sent requests</p>
                  ) : (
                    <div className="space-y-2">
                      {familyRequests.outgoing.map((req) => (
                        <FamilyRequestCard
                          key={req.relationship_id}
                          request={req}
                          type="outgoing"
                          onCancel={handleCancelFamily}
                        />
                      ))}
                    </div>
                  )}
                </div>
              </TabsContent>
            </Tabs>
            </div>
          </TabsContent>

          {/* All Members Tab */}
          <TabsContent value="members" className="mt-4">
            <MembersTab currentUser={user} />
          </TabsContent>
        </Tabs>

        {/* Add Family from Friend dialog */}
        {addFamilyTarget && (
          <AddFamilyFromFriendDialog
            friend={addFamilyTarget}
            onClose={() => setAddFamilyTarget(null)}
            onSuccess={() => { setAddFamilyTarget(null); loadAll(); }}
          />
        )}
      </div>
    </div>
  );
}
